/** Ignore status-bar / browser-chrome jitter; real keyboards are much taller. */
export const EARLY_EXIT_VIEWPORT_INSET_THRESHOLD_PX = 80;
export const EARLY_EXIT_IOS_STABLE_FRAMES = 2;
export const EARLY_EXIT_IOS_SETTLE_TIMEOUT_MS = 800;

export type EarlyExitVisibleViewport = {
  offsetTop: number;
  height: number;
};

export type EarlyExitModalViewportStyle = {
  height: string;
  top: string;
  keyboardOpen: boolean;
};

export type EarlyExitViewportSnapshot = {
  innerHeight: number;
  visualHeight: number;
  offsetTop: number;
};

export function readEarlyExitVisibleViewport(
  innerHeight: number,
  visualViewport?: { offsetTop: number; height: number } | null
): EarlyExitVisibleViewport {
  if (!visualViewport) {
    return { offsetTop: 0, height: innerHeight };
  }
  return {
    offsetTop: visualViewport.offsetTop,
    height: visualViewport.height,
  };
}

export function readEarlyExitViewportSnapshot(
  innerHeight: number,
  visualViewport?: { offsetTop: number; height: number } | null
): EarlyExitViewportSnapshot {
  const visible = readEarlyExitVisibleViewport(innerHeight, visualViewport);
  return {
    innerHeight,
    visualHeight: visible.height,
    offsetTop: visible.offsetTop,
  };
}

export function isEarlyExitViewportUnchanged(
  previous: EarlyExitViewportSnapshot,
  current: EarlyExitViewportSnapshot
): boolean {
  return (
    previous.innerHeight === current.innerHeight &&
    previous.visualHeight === current.visualHeight &&
    previous.offsetTop === current.offsetTop
  );
}

/**
 * Size the fullscreen modal to the visible viewport once.
 * Uses min(innerHeight, visualViewport.height) so native WebView resize
 * and overlay/pan do not stack into a double keyboard gap.
 */
export function resolveEarlyExitModalViewportStyle(
  innerHeight: number,
  visualViewport?: { offsetTop: number; height: number } | null
): EarlyExitModalViewportStyle {
  const visible = readEarlyExitVisibleViewport(innerHeight, visualViewport);
  const inset = innerHeight - visible.height;
  const keyboardOpen = inset >= EARLY_EXIT_VIEWPORT_INSET_THRESHOLD_PX || visible.offsetTop > 0;

  if (!keyboardOpen) {
    return { height: '100%', top: '0px', keyboardOpen: false };
  }

  return {
    height: `${visible.height}px`,
    top: `${visible.offsetTop}px`,
    keyboardOpen: true,
  };
}

export function scrollEarlyExitInputIntoView(el: HTMLElement | null): void {
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

/**
 * iOS keeps the full webview height until the keyboard animation is almost
 * done. Two unchanged frames at the *pre-keyboard* size are not settled —
 * wait until the viewport actually changes, then until it holds still.
 */
export async function waitForEarlyExitIosKeyboardSettle({
  readSnapshot,
  raf,
  now = () => Date.now(),
  stableFrames = EARLY_EXIT_IOS_STABLE_FRAMES,
  timeoutMs = EARLY_EXIT_IOS_SETTLE_TIMEOUT_MS,
}: {
  readSnapshot: () => EarlyExitViewportSnapshot;
  raf: (cb: FrameRequestCallback) => number;
  now?: () => number;
  stableFrames?: number;
  timeoutMs?: number;
}): Promise<'resized' | 'timeout'> {
  const initial = readSnapshot();
  const deadline = now() + timeoutMs;
  let last = initial;
  let seenChange = false;
  let held = 0;

  return new Promise(resolve => {
    const step: FrameRequestCallback = () => {
      const current = readSnapshot();
      if (!isEarlyExitViewportUnchanged(initial, current)) {
        seenChange = true;
      }
      if (isEarlyExitViewportUnchanged(last, current)) {
        held += 1;
      } else {
        last = current;
        held = 0;
      }
      if (seenChange && held >= stableFrames) {
        resolve('resized');
        return;
      }
      if (now() >= deadline) {
        resolve('timeout');
        return;
      }
      raf(step);
    };
    raf(step);
  });
}

export async function waitForEarlyExitKeyboardSettleIfNeeded(
  isIos: boolean,
  deps: Parameters<typeof waitForEarlyExitIosKeyboardSettle>[0]
): Promise<'resized' | 'timeout' | 'skipped'> {
  if (!isIos) return 'skipped';
  return waitForEarlyExitIosKeyboardSettle(deps);
}

export function resolveEarlyExitContentScrollTop({
  scrollTop,
  scrollRect,
  rowRect,
  padding = 12,
}: {
  scrollTop: number;
  scrollRect: { top: number; bottom: number };
  rowRect: { top: number; bottom: number };
  padding?: number;
}): number {
  const overflowBottom = rowRect.bottom - (scrollRect.bottom - padding);
  if (overflowBottom > 0) {
    return scrollTop + overflowBottom;
  }
  const overflowTop = scrollRect.top + padding - rowRect.top;
  if (overflowTop > 0) {
    return Math.max(0, scrollTop - overflowTop);
  }
  return scrollTop;
}

type IonContentScrollHost = {
  getScrollElement?: () => Promise<HTMLElement>;
  scrollToPoint?: (x: number, y: number, duration?: number) => Promise<void>;
  $el?: IonContentScrollHost;
};

const unwrapIonContent = (content: IonContentScrollHost | null): IonContentScrollHost | null => content?.$el ?? content;

export async function scrollEarlyExitRowIntoContent(
  row: HTMLElement | null,
  content: IonContentScrollHost | null
): Promise<void> {
  const host = unwrapIonContent(content);
  if (!row || !host?.getScrollElement) {
    scrollEarlyExitInputIntoView(row);
    return;
  }
  const scrollEl = await host.getScrollElement();
  const nextTop = resolveEarlyExitContentScrollTop({
    scrollTop: scrollEl.scrollTop,
    scrollRect: scrollEl.getBoundingClientRect(),
    rowRect: row.getBoundingClientRect(),
  });
  if (nextTop === scrollEl.scrollTop) return;
  if (typeof host.scrollToPoint === 'function') {
    await host.scrollToPoint(0, nextTop, 0);
    return;
  }
  scrollEl.scrollTop = nextTop;
}
